import { useTranslation } from 'next-i18next'
import React, { useEffect, useState } from 'react'

import { BeltVisual } from '@goodfellas/belt-visual'
import { Colors } from '@goodfellas/graduation'

import { goodfellasApi } from '../../apis'
import { useStore } from '../../functions/store'

interface GraduationHistoryEntry {
  belt: string
  degree: number
  graduatedAt: string
  source: string
  previousBelt?: string
  previousDegree?: number
}

export default function GraduationHistory() {
  const { t } = useTranslation('avaliacao360')
  const user = useStore((state) => state)
  const [history, setHistory] = useState<GraduationHistoryEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setLoading(true)
        setError(null)
        const response = await goodfellasApi.get(
          '/evaluation/graduation/history',
          {
            validateStatus: () => true,
          },
        )

        if (
          response.status >= 200 &&
          response.status < 300 &&
          response.data?.success
        ) {
          setHistory(response.data.data || [])
        } else {
          setError('Erro ao carregar histórico')
        }
      } catch {
        setError('Erro ao carregar histórico')
      } finally {
        setLoading(false)
      }
    }

    fetchHistory()
  }, [user.belt, user.degree])

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(date)
  }

  const getSourceLabel = (source: string) => {
    const sourceMap: Record<string, string> = {
      'bot.graduar': 'Bot WhatsApp',
      'profile-update': 'Atualização de Perfil',
      'evaluation.tables': 'Avaliação 360',
    }
    return sourceMap[source] || source
  }

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-white/5">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-br from-emerald-500/30 to-emerald-600/20 rounded-xl flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-emerald-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-white">
              Histórico de Graduações
            </h3>
            <p className="text-xs sm:text-sm text-slate-400">
              Suas últimas mudanças de faixa
            </p>
          </div>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-emerald-500 border-t-transparent" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-white/5">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-br from-emerald-500/30 to-emerald-600/20 rounded-xl flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-emerald-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-white">
              Histórico de Graduações
            </h3>
            <p className="text-xs sm:text-sm text-slate-400">
              Suas últimas mudanças de faixa
            </p>
          </div>
        </div>
        <div className="text-center py-8 text-slate-400 text-sm">{error}</div>
      </div>
    )
  }

  if (history.length === 0) {
    return (
      <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-white/5">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-br from-emerald-500/30 to-emerald-600/20 rounded-xl flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-emerald-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-white">
              Histórico de Graduações
            </h3>
            <p className="text-xs sm:text-sm text-slate-400">
              Suas últimas mudanças de faixa
            </p>
          </div>
        </div>
        <div className="text-center py-8 text-slate-400 text-sm">
          Nenhuma graduação registrada ainda
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-white/5">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-gradient-to-br from-emerald-500/30 to-emerald-600/20 rounded-xl flex items-center justify-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-emerald-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
            />
          </svg>
        </div>
        <div>
          <h3 className="text-lg sm:text-xl font-bold text-white">
            Histórico de Graduações
          </h3>
          <p className="text-xs sm:text-sm text-slate-400">
            Suas últimas mudanças de faixa
          </p>
        </div>
      </div>

      <div className="space-y-3">
        {history.map((entry, index) => (
          <div
            key={index}
            className="bg-slate-700/30 rounded-xl p-4 border border-white/5 hover:border-emerald-500/20 transition-all"
          >
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4 flex-1 min-w-0">
                {entry.previousBelt && (
                  <>
                    <div className="flex-shrink-0">
                      <BeltVisual
                        color={(entry.previousBelt as Colors) || Colors.White}
                        degree={entry.previousDegree || 0}
                        size="sm"
                      />
                    </div>
                    <div className="flex-shrink-0 text-slate-500">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 7l5 5m0 0l-5 5m5-5H6"
                        />
                      </svg>
                    </div>
                  </>
                )}
                <div className="flex-shrink-0">
                  <BeltVisual
                    color={(entry.belt as Colors) || Colors.White}
                    degree={entry.degree || 0}
                    size="sm"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-white truncate">
                    {formatDate(entry.graduatedAt)}
                  </div>
                  <div className="text-xs text-slate-400 truncate">
                    {getSourceLabel(entry.source)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
